/**
 * Canonical failure-reason code registry (T-014 / AC-B4).
 *
 * KNOWN_REASON_CODES is the FE sync-point with the backend ReasonCode.cs enum.
 * The FE drift-guard Karma test asserts this array matches the backend 22-code set.
 * When the backend adds or renames a constant, the NUnit reflection test (AC-B4 backend)
 * will fail, and KNOWN_REASON_CODES must be updated here in the same PR.
 *
 * Note: 'NoRef/Pred' and 'dE-CI' contain non-identifier characters — they are
 * string values serialized by JsonStringEnumConverter, not TypeScript identifiers.
 */

import { ReasonDetail, Scan } from '../../../model';

/** All 22 canonical reason codes as serialized on the wire (camelCase from JsonStringEnumConverter). */
export const KNOWN_REASON_CODES = [
  'NoModel',
  'NoImage',
  'NoMaterial',
  'ReferenceL',
  'NoRef/Pred',
  'BL',
  'BS',
  'CI',
  'dE-CI',
  'dL',
  'dA',
  'dB',
  'dC',
  'dE',
  'xdE',
  'xdBL',
  'xdL',
  'xdA',
  'xdB',
  'MlError',
  'Stain',
  'StainCheckError',
] as const;

/**
 * Exact set of MlError detail strings that indicate a bad-bucket-position failure.
 * Using Set.has for exact match — mirrors backend Inspector.cs `==` comparison (DL-011).
 * SAFETY: do NOT use .includes() — that is a substring check which over-matches.
 */
export const ML_BAD_BUCKET_DETAILS = new Set<string>([
  'BadBucketPosition',
  'NoBucketContourDetected',
]);

/**
 * Returns true if the scan has an MlError reason whose detail is one of the
 * known bad-bucket detail strings (exact match, Set.has — DL-011).
 *
 * Safe to call with a scan that has no reasons or null/undefined reasons.
 */
export function isBadBucketScan(scan: Scan): boolean {
  return (scan.reasons ?? []).some(
    (r) => r.code === 'MlError' && r.detail != null && ML_BAD_BUCKET_DETAILS.has(r.detail)
  );
}

/**
 * Maps reason severity to ng-zorro alert type.
 * Used by FailureReasonComponent — isolated here so it is unit-testable.
 */
export function severityToNzType(
  severity: ReasonDetail['severity']
): 'success' | 'info' | 'warning' | 'error' {
  switch (severity) {
    case 'Info':
      return 'info';
    case 'Warning':
      return 'warning';
    case 'Error':
    case 'Fatal':
      return 'error';
    default:
      return 'warning';
  }
}
